import React, { useState, useRef } from "react";
import { X, Check } from "lucide-react";
import ReCAPTCHA from "react-google-recaptcha";
import api from "../utils/api";

const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || "";

const NewsletterWidget = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
    const [errorMsg, setErrorMsg] = useState<string>("");
    const recaptchaRef = useRef<ReCAPTCHA>(null);

    const [formData, setFormData] = useState({
        firstName: "",
        lastName: "",
        email: "",
        interests: [] as string[],
    });

    const interestsOptions = ["Renter", "Owner", "Investor", "Other"];

    const toggleInterest = (interest: string) => {
        setFormData((prev) => ({
            ...prev,
            interests: prev.interests.includes(interest)
                ? prev.interests.filter((i) => i !== interest)
                : [...prev.interests, interest],
        }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setStatus("loading");
        setErrorMsg("");

        try {
            const captchaToken = await recaptchaRef.current?.executeAsync();
            recaptchaRef.current?.reset();

            if (!captchaToken) {
                setStatus("error");
                setErrorMsg("reCAPTCHA verification failed. Please try again.");
                return;
            }

            const res = await api.post("/content/newsletter", {
                ...formData,
                captchaToken,
            });

            if (res.data.success) {
                setStatus("success");
                setTimeout(() => {
                    setIsOpen(false);
                    setStatus("idle");
                    setFormData({ firstName: "", lastName: "", email: "", interests: [] });
                }, 3000);
            } else {
                setStatus("error");
                setErrorMsg("Something went wrong. Please try again.");
            }
        } catch (error) {
            console.error(error);
            setStatus("error");
            setErrorMsg("Something went wrong. Please try again.");
        }
    };

    return (
        <>
            {/* --- BANNER --- */}
            <div className="bg-gray-900 text-white py-12 px-6">
                <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-8">
                    <div className="max-w-xl text-center md:text-left">
                        <h2 className="text-2xl md:text-3xl font-serif leading-tight mb-2">
                            Subscribe to our newsletter
                        </h2>
                        <p className="text-gray-400 font-light text-lg">
                            Receive <span className="italic text-white">exclusive offers</span> & last-minute vacations directly in your inbox.
                        </p>
                    </div>
                    <div className="flex flex-col items-center md:items-end gap-4">
                        <button
                            onClick={() => setIsOpen(true)}
                            className="bg-white text-gray-900 px-8 py-3 rounded-full font-bold text-sm hover:bg-[#E84C5B] hover:text-white transition-all transform hover:scale-105 shadow-lg w-full md:w-auto"
                        >
                            Subscribe
                        </button>
                    </div>
                </div>
            </div>

            {/* --- MODAL --- */}
            {isOpen && (
                <div className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in">
                    <div
                        className="bg-white text-gray-900 rounded-[2rem] w-full max-w-2xl p-8 md:p-12 shadow-2xl relative animate-zoom-in"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <button
                            onClick={() => setIsOpen(false)}
                            className="absolute top-6 right-6 p-2 rounded-full hover:bg-gray-100 text-gray-400 hover:text-gray-900 transition-colors"
                        >
                            <X className="w-6 h-6" />
                        </button>

                        <div className="text-center md:text-left mb-8">
                            <h3 className="text-3xl font-serif font-bold mb-2">Stay Connected</h3>
                            <p className="text-gray-500">Join our exclusive community for updates and news.</p>
                        </div>

                        {status === "success" ? (
                            <div className="py-10 text-center animate-fade-in">
                                <div className="w-20 h-20 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-6">
                                    <Check className="w-10 h-10" />
                                </div>
                                <h4 className="text-2xl font-serif font-bold mb-2">Welcome aboard!</h4>
                                <p className="text-gray-500">You have successfully subscribed to our lists.</p>
                            </div>
                        ) : (
                            <form onSubmit={handleSubmit} className="space-y-6">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div className="space-y-2">
                                        <label className="text-xs font-bold text-gray-500 uppercase tracking-wider ml-1">First Name</label>
                                        <input
                                            type="text"
                                            required
                                            value={formData.firstName}
                                            onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                                            className="w-full bg-gray-50 border border-gray-200 rounded-xl p-3 focus:border-[#E84C5B] focus:ring-1 focus:ring-[#E84C5B] outline-none transition-all placeholder:text-gray-400 text-gray-900"
                                            placeholder="John"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-xs font-bold text-gray-500 uppercase tracking-wider ml-1">Last Name</label>
                                        <input
                                            type="text"
                                            required
                                            value={formData.lastName}
                                            onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                                            className="w-full bg-gray-50 border border-gray-200 rounded-xl p-3 focus:border-[#E84C5B] focus:ring-1 focus:ring-[#E84C5B] outline-none transition-all placeholder:text-gray-400 text-gray-900"
                                            placeholder="Doe"
                                        />
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <label className="text-xs font-bold text-gray-500 uppercase tracking-wider ml-1">Email Address</label>
                                    <input
                                        type="email"
                                        required
                                        value={formData.email}
                                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                        className="w-full bg-gray-50 border border-gray-200 rounded-xl p-3 focus:border-[#E84C5B] focus:ring-1 focus:ring-[#E84C5B] outline-none transition-all placeholder:text-gray-400 text-gray-900"
                                        placeholder="john@example.com"
                                    />
                                </div>

                                <div className="space-y-3">
                                    <label className="text-xs font-bold text-gray-500 uppercase tracking-wider ml-1">I am interested in...</label>
                                    <div className="flex flex-wrap gap-3">
                                        {interestsOptions.map((option) => {
                                            const isSelected = formData.interests.includes(option);
                                            return (
                                                <button
                                                    type="button"
                                                    key={option}
                                                    onClick={() => toggleInterest(option)}
                                                    className={`px-4 py-2 rounded-full text-sm font-bold flex items-center gap-2 transition-all border ${
                                                        isSelected
                                                            ? "bg-[#E84C5B] border-[#E84C5B] text-white shadow-md"
                                                            : "bg-white border-gray-200 text-gray-500 hover:border-gray-300 hover:bg-gray-50"
                                                    }`}
                                                >
                                                    {option}
                                                    {isSelected && <Check className="w-3 h-3" />}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>

                                {errorMsg && (
                                    <p className="text-red-500 text-sm text-center">{errorMsg}</p>
                                )}

                                {/* reCAPTCHA invisible — nessun checkbox visibile */}
                                <ReCAPTCHA
                                    ref={recaptchaRef}
                                    sitekey={RECAPTCHA_SITE_KEY}
                                    size="invisible"
                                />

                                <div className="flex justify-end items-center gap-4 pt-6 mt-2 border-t border-gray-100">
                                    <button
                                        type="button"
                                        onClick={() => setIsOpen(false)}
                                        className="text-gray-500 font-bold hover:text-gray-900 transition-colors text-sm"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={status === "loading"}
                                        className="bg-[#E84C5B] text-white px-8 py-3 rounded-full font-bold hover:bg-gray-900 transition-colors disabled:opacity-50 flex items-center gap-2 shadow-lg"
                                    >
                                        {status === "loading" ? "Submitting..." : "Confirm Subscription"}
                                    </button>
                                </div>
                            </form>
                        )}
                    </div>
                </div>
            )}
        </>
    );
};

export default NewsletterWidget;
